import { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Loader2,
  X,
  MailCheck,
  ShieldOff,
  Phone,
  KeyRound,
  ArrowLeft,
  Pencil,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiError } from "../lib/api";
import {
  isFirebaseConfigured,
  sendEmailResetLink,
  sendPhoneOtp,
  friendlyFirebaseError,
  isInsideIframe,
  clearRecaptcha,
} from "../lib/firebase";

// Same country code list as Register.jsx so the lookup payload matches what's
// stored in `cafes.owner_country_code` / `users.country_code`.
const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+966", label: "🇸🇦 +966" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+61", label: "🇦🇺 +61" },
];

/**
 * Forgot-password modal with two tabs:
 *  • Email link — sends a Firebase magic link to the user's email.
 *  • Phone OTP — Firebase phone auth + backend /auth/phone-reset.
 */
export default function ForgotPasswordModal({ open, onClose, initialEmail = "" }) {
  return (
    <AnimatePresence>
      {open && (
        <ForgotPasswordModalInner
          onClose={onClose}
          initialEmail={initialEmail}
        />
      )}
    </AnimatePresence>
  );
}

function ForgotPasswordModalInner({ onClose, initialEmail }) {
  const [mode, setMode] = useState("email"); // "email" | "phone"
  const fbReady = useMemo(() => isFirebaseConfigured(), []);

  const closeAll = () => {
    // Always clean up the invisible reCAPTCHA when the modal goes away so the
    // next open mounts a fresh verifier.
    clearRecaptcha();
    onClose?.();
  };

  return (
    <motion.div
      data-testid="forgot-password-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      onClick={closeAll}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 12, scale: 0.97 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="w-full max-w-md bg-[#121a16] border border-white/10 rounded-2xl shadow-[0_30px_60px_rgba(0,0,0,0.5)] p-7 relative"
        onClick={(ev) => ev.stopPropagation()}
      >
        <button
          type="button"
          onClick={closeAll}
          data-testid="forgot-modal-close"
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {!fbReady ? (
          <Unconfigured onClose={closeAll} />
        ) : (
          <>
            <div className="mb-5">
              <h2 className="text-xl font-black text-white mb-1">Forgot password</h2>
              <p className="text-xs text-gray-500">
                Pick how you&apos;d like to reset.
              </p>
            </div>

            <div
              className="grid grid-cols-2 gap-2 mb-5 p-1 rounded-xl bg-black/30 border border-white/5"
              data-testid="forgot-mode-tabs"
            >
              <TabBtn
                active={mode === "email"}
                onClick={() => setMode("email")}
                testId="forgot-tab-email"
                icon={<Mail className="w-4 h-4" />}
                label="Email link"
              />
              <TabBtn
                active={mode === "phone"}
                onClick={() => setMode("phone")}
                testId="forgot-tab-phone"
                icon={<Phone className="w-4 h-4" />}
                label="Phone"
              />
            </div>

            {mode === "email" ? (
              <EmailResetPanel
                initialEmail={initialEmail}
                onClose={closeAll}
              />
            ) : (
              <PhoneResetBlockedPanel onClose={closeAll} onSwitchToEmail={() => setMode("email")} />
            )}
          </>
        )}

        {/* Stable host for the invisible reCAPTCHA. On every Send-OTP click we
            wipe its children and append a fresh child with a unique id, then
            point RecaptchaVerifier at that child. This way Firebase always
            sees a "new" element (no "already rendered" error) AND Google's
            iframe is never ripped out of the DOM mid-flight (no .style on
            null crash). */}
        <div id="phone-otp-recaptcha-host" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }} aria-hidden="true" />
      </motion.div>
    </motion.div>
  );
}

function TabBtn({ active, onClick, testId, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors " +
        (active
          ? "bg-amber-500 text-black"
          : "text-gray-400 hover:text-white")
      }
    >
      {icon}
      {label}
    </button>
  );
}

function Unconfigured({ onClose }) {
  return (
    <div className="text-center space-y-3" data-testid="forgot-unconfigured">
      <ShieldOff className="w-10 h-10 mx-auto text-amber-400" />
      <h2 className="text-xl font-black text-white">
        Password reset unavailable
      </h2>
      <p className="text-sm text-gray-400">
        Password reset isn&apos;t configured yet. Please contact your platform admin.
      </p>
      <Button
        onClick={onClose}
        className="mt-2 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
        data-testid="forgot-unconfigured-close"
      >
        OK
      </Button>
    </div>
  );
}

// ---------- Phone-blocked panel ----------
// Password reset is intentionally EMAIL-ONLY. If a user clicks the "Phone" tab,
// we show a friendly notice pointing them back to the email flow instead of
// letting them start an OTP round-trip that the server would reject anyway.
function PhoneResetBlockedPanel({ onClose, onSwitchToEmail }) {
  return (
    <div className="text-center space-y-4" data-testid="forgot-phone-blocked">
      <ShieldOff className="w-10 h-10 mx-auto text-amber-400" />
      <h3 className="text-lg font-black text-white">
        Reset only with registered email only
      </h3>
      <p className="text-sm text-gray-400">
        For your account&apos;s security, we send password-reset links exclusively
        to the email address you registered with. Phone-based reset isn&apos;t
        supported.
      </p>
      <div className="flex items-center justify-center gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          data-testid="forgot-phone-blocked-cancel"
          className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-2"
        >
          Cancel
        </button>
        <Button
          onClick={onSwitchToEmail}
          className="bg-[#d4af37] hover:bg-[#e2c258] text-black font-black uppercase tracking-widest"
          data-testid="forgot-phone-blocked-use-email"
        >
          Use email instead
        </Button>
      </div>
    </div>
  );
}

// ---------- Email panel ----------
function EmailResetPanel({ initialEmail, onClose }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setErr("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      try {
        const { data: lookup } = await api.post("/auth/email-reset/lookup", {
          email: trimmed,
        });
        if (!lookup?.registered) {
          setErr(
            "This email isn't registered to any account. Please check the address you typed.",
          );
          setBusy(false);
          return;
        }
      } catch (lookupErr) {
        console.warn("[email-reset] lookup pre-check failed; continuing", lookupErr);
      }
      await sendEmailResetLink(trimmed);
      setDone(true);
    } catch (e2) {
      setErr(friendlyFirebaseError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center space-y-4" data-testid="forgot-email-sent">
        <MailCheck className="w-12 h-12 mx-auto text-emerald-400" />
        <h3 className="text-xl font-black text-white">Check your inbox</h3>
        <p className="text-sm text-gray-400">
          We&apos;ve sent a sign-in link to{" "}
          <span className="text-white font-semibold">{email}</span>. Open the
          email and tap the button inside.
        </p>
        <Button
          onClick={onClose}
          className="mt-2 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
          data-testid="forgot-email-sent-close"
        >
          Got it
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-email-form">
      <p className="text-sm text-gray-400">
        Enter the email you registered with. We&apos;ll email you a one-time
        sign-in link.
      </p>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">Email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            data-testid="forgot-email-input"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-amber-500/60"
          />
        </div>
      </div>
      {err && (
        <div
          data-testid="forgot-email-error"
          className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
        >
          {err}
        </div>
      )}
      <div className="flex items-center justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onClose}
          data-testid="forgot-email-cancel"
          disabled={busy}
          className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-2 disabled:opacity-50"
        >
          Cancel
        </button>
        <Button
          type="submit"
          disabled={busy}
          data-testid="forgot-email-submit"
          className="bg-[#d4af37] hover:bg-[#e2c258] text-black font-black uppercase tracking-widest"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending…
            </>
          ) : (
            "Send reset link"
          )}
        </Button>
      </div>
    </form>
  );
}

// ---------- Phone panel ----------
function PhoneResetPanel({ onClose }) {
  // Step state: "phone" → enter number → send OTP
  //             "otp"   → enter 6-digit code → confirm + capture ID token
  //             "password" → set new password → POST /auth/phone-reset
  //             "done"  → success screen
  const [step, setStep] = useState("phone");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");

  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [err, setErr] = useState("");

  // Holds the Firebase ConfirmationResult between step "phone" → "otp"
  const [confirmation, setConfirmation] = useState(null);
  const [idToken, setIdToken] = useState("");

  // Each "Send OTP" click mounts a brand-new container so Firebase never
  // sees a stale grecaptcha widget on the same element. The id is suffixed
  // with a counter so retries don't collide.
  const [recaptchaSeq, setRecaptchaSeq] = useState(0);

  // Cooldown between Send/Resend OTP clicks (seconds). Prevents users from
  // hammering Firebase and getting throttled with auth/too-many-requests.
  const RESEND_COOLDOWN_SEC = 30;
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Core "send OTP" routine. `isResend=true` skips the lookup pre-check since
  // we already validated the number on the first send.
  const fireOtp = async ({ isResend = false } = {}) => {
    setErr("");
    const digits = (phone || "").replace(/\D/g, "");
    if (!digits) {
      setErr("Please enter your phone number.");
      return;
    }
    if (isInsideIframe()) {
      setErr(
        "Phone OTP doesn't work inside this embedded preview. Open the app in a real browser tab and try again.",
      );
      return;
    }
    setBusy(true);
    setProgress(isResend ? "Resending…" : "Checking number…");
    try {
      if (!isResend) {
        const { data: lookup } = await api.post("/auth/phone-reset/lookup", {
          country_code: countryCode,
          phone: digits,
        });
        if (!lookup?.registered) {
          setErr(
            "This phone number isn't linked to any account. (Cafe owners: use the same number you signed up with.)",
          );
          setBusy(false);
          setProgress("");
          return;
        }
      }

      const e164 = `${countryCode}${digits}`;
      // Mount a fresh inner container inside the stable host. We do NOT remove
      // earlier verifier iframes from the DOM here — Google's recaptcha script
      // may still hold references to them and crashes with "Cannot read .style
      // on null" if its iframe is yanked. Instead we just call clear() on the
      // previous verifier (which lets Google tear down its iframe gracefully)
      // and then point a brand-new RecaptchaVerifier at a brand-new child id.
      clearRecaptcha();
      const host = document.getElementById("phone-otp-recaptcha-host");
      if (host) host.replaceChildren();
      const nextSeq = recaptchaSeq + 1;
      setRecaptchaSeq(nextSeq);
      const nextContainerId = `phone-otp-recaptcha-${nextSeq}`;
      const inner = document.createElement("div");
      inner.id = nextContainerId;
      (host || document.body).appendChild(inner);
      const conf = await sendPhoneOtp(e164, {
        onProgress: (msg) => setProgress(msg),
        containerId: nextContainerId,
      });
      setConfirmation(conf);
      setOtp(""); // clear any old code so the user can't accidentally submit it
      setCooldown(RESEND_COOLDOWN_SEC);
      if (!isResend) setStep("otp");
    } catch (e2) {
      setErr(friendlyFirebaseError(e2));
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const sendOtp = async (e) => {
    e?.preventDefault();
    await fireOtp({ isResend: false });
  };

  const resendOtp = async () => {
    if (cooldown > 0 || busy) return;
    await fireOtp({ isResend: true });
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setErr("");
    const code = (otp || "").replace(/\D/g, "");
    if (code.length < 4) {
      setErr("Please enter the OTP from your SMS.");
      return;
    }
    if (!confirmation) {
      setErr("Verification expired — please request a new OTP.");
      setStep("phone");
      return;
    }
    setBusy(true);
    setProgress("Verifying code…");
    try {
      const result = await confirmation.confirm(code);
      const token = await result.user.getIdToken(true);
      setIdToken(token);
      setStep("password");
    } catch (e2) {
      setErr(friendlyFirebaseError(e2));
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const setNewPassword = async (e) => {
    e?.preventDefault();
    setErr("");
    if (pwd.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pwd !== pwd2) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    setProgress("Updating password…");
    try {
      await api.post("/auth/phone-reset", {
        firebase_id_token: idToken,
        new_password: pwd,
      }, { retryOnNetworkError: true });
      setStep("done");
    } catch (e2) {
      setErr(formatApiError(e2) || "Couldn't update password.");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  if (step === "done") {
    return (
      <div className="text-center space-y-4" data-testid="forgot-phone-done">
        <KeyRound className="w-12 h-12 mx-auto text-emerald-400" />
        <h3 className="text-xl font-black text-white">Password updated</h3>
        <p className="text-sm text-gray-400">
          You can now sign in with your new password.
        </p>
        <Button
          onClick={onClose}
          className="mt-2 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
          data-testid="forgot-phone-done-close"
        >
          Back to sign in
        </Button>
      </div>
    );
  }

  if (step === "phone") {
    return (
      <form onSubmit={sendOtp} className="space-y-4" data-testid="forgot-phone-form">
        <p className="text-sm text-gray-400">
          Enter the phone number you signed up with. We&apos;ll text you a
          one-time code via Firebase.
        </p>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
            Phone
          </Label>
          <div className="flex gap-2">
            <select
              data-testid="forgot-phone-country"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="bg-black/40 border border-white/10 text-white rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/60"
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code} className="bg-[#121a16]">
                  {c.label}
                </option>
              ))}
            </select>
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                data-testid="forgot-phone-input"
                type="tel"
                inputMode="numeric"
                autoFocus
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="9876543210"
                className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-amber-500/60"
              />
            </div>
          </div>
          <p className="text-[11px] text-gray-500 tracking-wide">
            Standard SMS rates may apply. Powered by Firebase.
          </p>
        </div>

        {err && (
          <div
            data-testid="forgot-phone-error"
            className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
          >
            {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            data-testid="forgot-phone-cancel"
            disabled={busy}
            className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-2 disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={busy || cooldown > 0}
            data-testid="forgot-phone-send-otp"
            className="bg-[#d4af37] hover:bg-[#e2c258] text-black font-black uppercase tracking-widest disabled:opacity-60"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                {progress || "Sending…"}
              </>
            ) : cooldown > 0 ? (
              `Wait ${cooldown}s`
            ) : (
              "Send OTP"
            )}
          </Button>
        </div>
      </form>
    );
  }

  if (step === "otp") {
    const goEditPhone = () => {
      setStep("phone");
      setOtp("");
      setErr("");
      // Keep `phone` and `countryCode` state intact so the user can edit
      // the digits they typed instead of starting over from scratch.
      clearRecaptcha();
    };
    return (
      <form onSubmit={verifyOtp} className="space-y-4" data-testid="forgot-otp-form">
        <button
          type="button"
          onClick={goEditPhone}
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white"
          data-testid="forgot-otp-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Change number
        </button>
        <p className="text-sm text-gray-400">
          Enter the 6-digit code we just texted to{" "}
          <span className="text-white font-semibold">
            {countryCode} {phone}
          </span>
          <button
            type="button"
            onClick={goEditPhone}
            data-testid="forgot-otp-edit-phone"
            className="ml-2 inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs font-semibold uppercase tracking-wider"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
          .
        </p>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
            One-time code
          </Label>
          <Input
            data-testid="forgot-otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            className="bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-amber-500/60 text-center tracking-[0.4em] text-lg font-bold"
          />
        </div>

        {err && (
          <div
            data-testid="forgot-otp-error"
            className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
          >
            {err}
          </div>
        )}

        <Button
          type="submit"
          disabled={busy}
          data-testid="forgot-otp-verify"
          className="w-full bg-[#d4af37] hover:bg-[#e2c258] text-black font-black uppercase tracking-widest"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {progress || "Verifying…"}
            </>
          ) : (
            "Verify code"
          )}
        </Button>

        <div className="flex items-center justify-center text-xs text-gray-400 pt-1">
          {cooldown > 0 ? (
            <span data-testid="forgot-otp-resend-cooldown">
              Didn&apos;t get it? Resend in{" "}
              <span className="text-white font-semibold">{cooldown}s</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={resendOtp}
              disabled={busy}
              data-testid="forgot-otp-resend"
              className="text-amber-400 hover:text-amber-300 font-semibold uppercase tracking-wider disabled:opacity-50"
            >
              Resend OTP
            </button>
          )}
        </div>
      </form>
    );
  }

  // step === "password"
  return (
    <form onSubmit={setNewPassword} className="space-y-4" data-testid="forgot-newpwd-form">
      <p className="text-sm text-gray-400">
        Number verified. Choose a new password (at least 6 characters).
      </p>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
          New password
        </Label>
        <Input
          data-testid="forgot-newpwd-input"
          type="password"
          autoFocus
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="••••••••"
          className="bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-amber-500/60"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-gray-400">
          Confirm password
        </Label>
        <Input
          data-testid="forgot-newpwd-confirm"
          type="password"
          value={pwd2}
          onChange={(e) => setPwd2(e.target.value)}
          placeholder="••••••••"
          className="bg-black/40 border-white/10 text-white placeholder:text-gray-600 focus-visible:ring-amber-500/60"
        />
      </div>

      {err && (
        <div
          data-testid="forgot-newpwd-error"
          className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-md"
        >
          {err}
        </div>
      )}

      <Button
        type="submit"
        disabled={busy}
        data-testid="forgot-newpwd-submit"
        className="w-full bg-[#d4af37] hover:bg-[#e2c258] text-black font-black uppercase tracking-widest"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {progress || "Saving…"}
          </>
        ) : (
          "Update password"
        )}
      </Button>
    </form>
  );
}
